"use client";

import * as React from "react";
import { Check, ChevronsUpDown, Loader, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useCallback, useState, useEffect } from "react";
import { debounce } from "lodash";

export interface Option {
  value: string;
  label: string | React.ReactElement<any>;
  searchLabel?: string;
}

interface MultiSelectProps {
  options: Option[];
  value: string | string[];
  selectedOptions?: Option[];
  onChange: (value: string | string[]) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  width?: string;
  loading?: boolean;
  isInvalid?: boolean | string | null;
  isValid?: boolean | string | null;
  messageError?: string;
  showError?: boolean;
  disabled?: boolean;
  allowCreate?: boolean;
  onCreateOption?: (inputValue: string) => Promise<Option>;
  onSearch?: (searchTerm: string) => Promise<Option[]>;
  debounceTime?: number;
  multiple?: boolean;
  maxItems?: number;
}

const MessageError = ({
  isInvalid,
  messageError,
}: {
  isInvalid?: boolean | string | null;
  messageError?: string;
}) => {
  if (!isInvalid || !messageError) return null;
  return <p className="text-xs text-red-500 mt-1">{messageError}</p>;
};

export function MultiSelect({
  options,
  value,
  selectedOptions,
  onChange,
  onBlur,
  onFocus,
  placeholder = "Selecione...",
  searchPlaceholder = "Pesquisar...",
  emptyMessage = "Nenhum item encontrado.",
  className,
  loading = false,
  isInvalid,
  isValid,
  disabled,
  messageError,
  allowCreate = false,
  showError = true,
  onCreateOption,
  onSearch,
  debounceTime = 300,
  multiple = false,
  maxItems,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");
  const [isCreating, setIsCreating] = React.useState(false);
  const [searchResults, setSearchResults] = useState<Option[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const selectedValues = React.useMemo(() => {
    if (multiple) {
      return Array.isArray(value) ? value : value ? [value] : [];
    }
    return value ? [value] : [];
  }, [value, multiple]);

  const reachedMaxItems = React.useMemo(() => {
    return multiple && maxItems ? selectedValues.length >= maxItems : false;
  }, [multiple, maxItems, selectedValues]);

  const handleBlur = () => {
    setFocused(false);
    if (onBlur) onBlur();
  };

  const handleFocus = () => {
    setFocused(true);
    if (onFocus) onFocus();
  };

  const getLabel = (label: any, searchLabel?: string) => {
    return typeof label === "string" ? label : searchLabel || "";
  };

  const handleCreateOption = async () => {
    if (!onCreateOption || !inputValue) return;

    try {
      setIsCreating(true);
      const newOption = await onCreateOption(inputValue);

      if (multiple) {
        const flatSelectedValues = selectedValues.flat();

        const newValues = [...flatSelectedValues, newOption.value];
        onChange(newValues);
      } else {
        onChange(newOption.value);
      }

      setOpen(false);
      setInputValue("");
    } catch {
      // Keep the current input so the user can retry creating the option.
    } finally {
      setIsCreating(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce(async (searchTerm: string) => {
      if (!onSearch) return;

      try {
        setIsSearching(true);
        const results = await onSearch(searchTerm);
        setSearchResults(results);
      } catch {
        // Keep the previous results when the remote search is unavailable.
      } finally {
        setIsSearching(false);
      }
    }, debounceTime),
    [onSearch]
  );

  const allOptions = React.useMemo(() => {
    if (!selectedOptions || selectedOptions.length === 0) return options;

    const missingOptions = selectedOptions.filter(
      (selected) => !options.some((opt) => opt.value === selected.value)
    );

    if (missingOptions.length === 0) return options;
    return [...missingOptions, ...options];
  }, [options, selectedOptions]);

  const displayOptions = inputValue ? searchResults : allOptions;

  const removeItem = (itemValue: string, e?: React.MouseEvent) => {
    if (e) e.preventDefault();

    if (multiple) {
      const newValues = selectedValues.flat().filter((v) => v !== itemValue);

      onChange(newValues);
    }
  };

  const toggleItem = (optionValue: string) => {
    if (multiple) {
      const flatSelectedValues = selectedValues.flat();

      if (flatSelectedValues.includes(optionValue)) {
        removeItem(optionValue);
      } else {
        if (maxItems && flatSelectedValues.length >= maxItems) {
          return;
        }

        const newValues = [...flatSelectedValues, optionValue];
        onChange(newValues);
      }
    } else {
      if (value === optionValue) {
        onChange("");
      } else {
        onChange(optionValue);
      }
    }

    if (!multiple) {
      setOpen(false);
    }
    setInputValue("");
  };

  useEffect(() => {
    if (onSearch && inputValue) {
      debouncedSearch(inputValue);
    } else if (inputValue) {
      const result = allOptions.filter((option) => {
        const labelText = getLabel(option.label, option.searchLabel);
        return labelText.toLowerCase().includes(inputValue.toLowerCase());
      });
      setSearchResults(result);
    } else {
      setSearchResults([]);
    }
  }, [inputValue, onSearch, debouncedSearch, allOptions]);

  const getLabelByValue = (optionValue: string): React.ReactNode => {
    const option = allOptions.find((opt) => opt.value === optionValue);
    return option?.label || optionValue;
  };

  return (
    <div className="space-y-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              `w-full justify-between text-zinc-600 min-h-10 h-auto`,
              focused
                ? "border-teal-500 ring-1 ring-teal-500"
                : "border-gray-300",
              className,
              {
                "is-invalid": isInvalid,
              },
              {
                "is-valid": isValid,
              }
            )}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={disabled}
          >
            <div className="flex flex-wrap gap-1 items-center max-w-[90%] overflow-hidden">
              {multiple && selectedValues.length > 0 ? (
                <div className="flex flex-wrap gap-1 max-w-full">
                  {selectedValues.map((val, index) => (
                    <div
                      key={index}
                      className="bg-muted text-muted-foreground px-2 py-0.5 rounded-md text-xs flex items-center"
                    >
                      {getLabelByValue(Array.isArray(val) ? val[0] : val)}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeItem(Array.isArray(val) ? val[0] : val, e);
                        }}
                        className="ml-1 hover:text-foreground"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : selectedValues.length > 0 ? (
                Array.isArray(selectedValues[0]) ? (
                  getLabelByValue(selectedValues[0][0])
                ) : (
                  getLabelByValue(selectedValues[0])
                )
              ) : (
                <span className="text-muted-foreground">{placeholder}</span>
              )}
            </div>
            {loading ? (
              <Loader className="ml-2 h-4 w-4 animate-spin" />
            ) : (
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" style={{ width: "var(--radix-popover-trigger-width)" }} className="w-full p-0">
          <Command className="w-full">
            <CommandInput
              placeholder={searchPlaceholder}
              onFocus={handleFocus}
              onBlur={handleBlur}
              value={inputValue}
              onValueChange={setInputValue}
            />
            <CommandList className="px-5">
              {loading || isSearching ? (
                <div className="p-4 text-center">Carregando...</div>
              ) : (
                <>
                  <CommandEmpty>
                    {allowCreate && onCreateOption ? (
                      <div className="flex flex-col gap-2 p-2">
                        <button
                          onClick={handleCreateOption}
                          disabled={isCreating || loading}
                          className="w-full text-zinc-700 py-1.5 px-3 bg-zinc-200/50 rounded"
                        >
                          {isCreating || loading ? (
                            <Loader className="mr-2 h-4 w-4 animate-spin" />
                          ) : null}
                          Criar "{inputValue}"
                        </button>
                      </div>
                    ) : (
                      emptyMessage
                    )}
                  </CommandEmpty>
                  <CommandGroup>
                    {displayOptions.map((option) => {
                      const isSelected = selectedValues.includes(option.value);
                      const isDisabled = !isSelected && reachedMaxItems;

                      return (
                        <CommandItem
                          key={option.value}
                          value={`${option.value}-${getLabel(
                            option.label,
                            option.searchLabel
                          )}`}
                          onSelect={() => toggleItem(option.value)}
                          disabled={isDisabled}
                          className={cn(
                            isSelected && "bg-accent/50",
                            isDisabled && "opacity-50 cursor-not-allowed"
                          )}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              isSelected ? "opacity-100" : "opacity-0"
                            )}
                          />
                          {option.label}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {showError && (
        <MessageError isInvalid={isInvalid} messageError={messageError} />
      )}
    </div>
  );
}
